import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const AuthContext = createContext(null);

// Always use relative URL - works on any domain
const API_URL = '/api';

// Auth is cookie-based (secure HttpOnly cookie set by backend). Always send cookies.
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (e) {
      console.error("Logout request failed (ignored, clearing session anyway):", e);
    }
    setUser(null);
    setPermissions([]);
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/permissions`);
      setPermissions(res.data.permissions || []);
    } catch (e) { setPermissions([]); }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      await fetchPermissions();
    } catch (error) {
      // Not authenticated (no valid cookie) - stay logged out
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password, totpCode = null) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password, totp_code: totpCode });
    // Backend sets the HttpOnly cookie; we only need the returned user object.
    if (response.data.user) {
      setUser(response.data.user);
      await fetchPermissions();
    }
    return response.data;
  }, [fetchPermissions]);

  // Sicherheit: automatische Abmeldung nach 30 Minuten Inaktivität.
  useEffect(() => {
    if (!user) return;
    const IDLE_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { logout(); }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const hasPerm = useCallback((perm) => permissions.includes(perm), [permissions]);
  const isAdmin = user?.role === "admin";
  const markOnboardingSeen = useCallback(
    () => setUser((u) => (u ? { ...u, onboarding_seen: "multiuser_v1" } : u)),
    []
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, permissions, hasPerm, isAdmin, markOnboardingSeen }),
    [user, loading, login, logout, permissions, hasPerm, isAdmin, markOnboardingSeen]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
